import type { CastListMarkerInfo } from './computeListMarkers';
import { extractCastFromParagraphText } from './extractCastFromParagraphText';
import { extractCastFromTable } from './extractCastFromTable';
import { extractParagraphPrimaryStyle } from './extractParagraphPrimaryStyle';
import type { AlineContentBlock, ParagraphElement } from '../../slate-api';
import {
  dynamicBlockType,
  instructionFieldElementType,
  paragraphType,
  tableType,
} from '../../slate-api';
import type { CastBlockContent, CastParagraph } from '../types';

export function extractCastFromContentBlocks(
  blocks: AlineContentBlock[],
  listMarkers: Map<ParagraphElement, CastListMarkerInfo>
): CastBlockContent[] {
  const results: CastBlockContent[] = [];

  for (const block of blocks) {
    switch (block.type) {
      case tableType:
        results.push(extractCastFromTable(block, listMarkers, extractCastFromContentBlocks));
        break;

      /**
       * Empty paragraphs are kept: a blank line is real document structure the
       * LLM needs to see — it anchors inserts (the only anchor in a blank
       * document) and tells the model the document's spacing conventions.
       */
      case paragraphType: {
        const paragraph: CastParagraph = {
          children: extractCastFromParagraphText(block),
          id: block.locationId ?? '',
          type: 'paragraph',
        };

        const primaryStyle = extractParagraphPrimaryStyle(block);
        if (primaryStyle) paragraph.primaryStyle = primaryStyle;

        /**
         * Markers are precomputed once from the query editor (see
         * computeListMarkers) so the Cast numbers match what the editor renders;
         * the loop only looks each paragraph's marker up by node.
         */
        const listInfo = listMarkers.get(block);
        if (listInfo) {
          paragraph.listLevel = listInfo.listLevel;
          paragraph.listType = listInfo.listType;
          paragraph.listMarker = listInfo.listMarker;
        }

        results.push(paragraph);
        break;
      }

      case dynamicBlockType:
      case instructionFieldElementType:
        if ('children' in block) {
          results.push(...extractCastFromContentBlocks(block.children, listMarkers));
        }
        break;

      /**
       * Other AlineContentBlock variants (e.g. TOC) carry no useful prose for
       * the Cast representation and are intentionally skipped rather than
       * throwing — keeps extraction resilient to schema additions.
       */
      default:
        break;
    }
  }

  return results;
}
